import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { isEntitledToBiasAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToBinScatterMultiSelect } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSolutions } from 'libs/end-to-end/src/group-entitlements';
import { isBinScatterMultiSelectEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FocusAreaConstArr } from 'libs/end-to-end/src/focus-area';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';

export interface EntitledDetailsForFocusAreaPage {
  areSolutionsEnabled: boolean;
  entitledToBiasAnalysis: boolean;
  summaryV2TabEnabled: boolean;
  summaryLegacyTabEnabled: boolean;
  binScatterMultiSelectEnabled: boolean;
  dataIntelligenceDeepDiveEnabled: boolean;
  atLeastOneSectionShowing: boolean;
  pageEnabled: boolean; // this will be false if none of the sections are enabled
}

export const determineEntitledDetailsForFocusAreaPage = ({
  observation,
  focusArea,
}: {
  observation: MyObservation | null;
  focusArea: FocusArea;
}): EntitledDetailsForFocusAreaPage => {
  const focusAreaDefinition = observation?.focusAreas.find(
    (fa) => fa.focusArea === focusArea,
  );

  if (!focusAreaDefinition || !focusAreaDefinition.isVisible) {
    return {
      areSolutionsEnabled: false,
      pageEnabled: false,
      atLeastOneSectionShowing: false,
      entitledToBiasAnalysis: false,
      binScatterMultiSelectEnabled: false,
      dataIntelligenceDeepDiveEnabled: false,
      summaryLegacyTabEnabled: false,
      summaryV2TabEnabled: false,
    };
  }

  const areSolutionsEnabled =
    !observation?.hideFocusAreaSolutionsPage &&
    isEntitledToSolutions({
      features: observation?.features ?? [],
      focusArea,
    });

  const entitledToBiasAnalysis = isEntitledToBiasAnalysis({
    features: observation?.features ?? [],
    focusArea,
  });

  const atLeastOneSectionShowing =
    entitledToBiasAnalysis || areSolutionsEnabled;

  const summaryV2TabEnabled =
    Boolean(observation?.showNewDataIntelligenceDeepDivePage) &&
    atLeastOneSectionShowing;

  const summaryLegacyTabEnabled =
    !observation?.showNewDataIntelligenceDeepDivePage &&
    atLeastOneSectionShowing;

  const binScatterMultiSelectEnabled =
    isBinScatterMultiSelectEnabledForRevision({
      revisionFlags: observation,
    }) &&
    isEntitledToBinScatterMultiSelect({
      features: observation?.features ?? [],
      focusArea,
    });

  const dataIntelligenceDeepDiveEnabled =
    Boolean(observation?.showNewDataIntelligenceDeepDivePage) &&
    entitledToBiasAnalysis;

  return {
    areSolutionsEnabled,
    entitledToBiasAnalysis,
    atLeastOneSectionShowing,
    summaryV2TabEnabled,
    summaryLegacyTabEnabled,
    binScatterMultiSelectEnabled,
    dataIntelligenceDeepDiveEnabled,
    pageEnabled:
      summaryV2TabEnabled ||
      summaryLegacyTabEnabled ||
      entitledToBiasAnalysis ||
      binScatterMultiSelectEnabled ||
      areSolutionsEnabled,
  };
};

/**
 * Return a list of MyFocusAreaDefinition sorted by an order defined
 * in-function. This is the order used for the left hand side menu.
 */
export const getFocusAreasInSortedOrder = ({
  focusAreas,
}: {
  focusAreas: MyFocusAreaDefinition[];
}) => {
  return focusAreas.sort((a, b) => {
    return (
      FocusAreaConstArr.indexOf(a.focusArea) -
      FocusAreaConstArr.indexOf(b.focusArea)
    );
  });
};
